import { RESULTS_STORIES } from "@/lib/results-stories";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { fullResults } from "@/lib/site-data";
import { Header } from "@/components/site/Header";
import { HeroResults } from "@/components/site/HeroResults";
import { Footer } from "@/components/site/Footer";
import { breadcrumbSchema, safeJsonLd } from "@/lib/structured-data";
import { QuoteBand } from "@/components/site/QuoteBand";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
const NorwoodQuiz = lazy(() => import("@/components/site/NorwoodQuiz").then(m => ({ default: m.NorwoodQuiz })));
const Guarantee = lazy(() => import("@/components/site/Guarantee").then(m => ({ default: m.Guarantee })));
const Faq = lazy(() => import("@/components/site/Faq").then(m => ({ default: m.Faq })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

export const Route = createFileRoute("/results/")({
  head: () => ({
    meta: [
      { title: "Men's Hair Transplant Before & After Results" },
      {
        name: "description",
        content:
          "See hair transplant results and before & after photos of celebrities & everyday men from RESTORE's FUE hair transplants. Free consults!",
      },
      { property: "og:title", content: "Hair Transplant Results — Before & After | RESTORE® Hair" },
      {
        property: "og:description",
        content:
          "Real RESTORE® hair transplant before & after results from athletes, celebrities and everyday clients. Over 20,000 procedures.",
      },
      { property: "og:url", content: "https://restorehair.com/results/" },
    ],
    links: [{ rel: "canonical", href: "https://restorehair.com/results/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd(breadcrumbSchema([{ name: "Results", path: "/results/" }])),
      },
    ],
  }),
  component: ResultsPage,
});

function ResultsPage() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main data-cta-location="results">
        <HeroResults />
        <QuoteBand />
        <Suspense fallback={null}>
          <Results
            items={fullResults}
            initialCount={24}
            showSeeMore
            batchSizes={[24, 24, 24, 9]}
            columns={3}
            seeMoreLabel="See More Hair Transplant Results"
            eyebrow="Hair Transplant Results: Before and After"
            heading={
              <span className="mb-[10px] block text-[30px] leading-none font-normal">
                100% authentic RESTORE hair transplant clients.
              </span>
            }
            subheading={
              <span className="block text-[30px] leading-none font-bold">
                No photoshopping. No trick photography.
              </span>
            }
            description={null}
            headlineAfterButton={
              <>
                <span className="block text-[30px] leading-none font-normal text-foreground">
                  100% authentic RESTORE® hair transplant surgery clients.
                </span>
                <span className="block text-[30px] leading-none font-bold text-foreground">
                  No photoshopping. No trick photography.
                </span>
              </>
            }
          />
          <ClientStories />
          <NorwoodQuiz />
          <Guarantee variant="results" />
          <Faq />
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
// Links to every results story page. The gallery above renders 24 cards on
// the server, so stories further down had no crawlable link.
const STORIES = RESULTS_STORIES;

function ClientStories() {
  return (
    <section className="bg-background py-10">
      <div className="mx-auto max-w-5xl px-4 text-center lg:px-8">
        <h2 className="text-2xl font-bold text-foreground">Client Stories</h2>
        <nav aria-label="Client stories" className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2">
          {STORIES.map((s) => (
            <a key={s.href} href={s.href} className="text-primary underline underline-offset-4 hover:no-underline">
              {s.name}
            </a>
          ))}
        </nav>
      </div>
    </section>
  );
}
